import { useCallback, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import oAuthAssistant, { oAuthScopes } from '@config/curity'
import { ApplicationState } from '@store/types'
import { setAccessToken } from '@store/session/actions'
import { initTrackRefin, logTrackRefinStep } from '@utils/trackRefin'
import { StepRefin } from '@utils/enums'

const useAuth = () => {
  const dispatch = useDispatch()
  const { accessToken, sessionId } = useSelector((state: ApplicationState) => state.session)
  const isApp = useSelector((state: ApplicationState) => state.ui.navigation.isApp)
  const [isLoading, setIsLoading] = useState(false)

  const logAuthError = useCallback(
    (step: StepRefin) => {
      if (sessionId) initTrackRefin(sessionId, isApp)
      logTrackRefinStep(step)
    },
    [sessionId, isApp],
  )

  const getToken = useCallback(() => {
    oAuthAssistant
      ?.init()
      .then(() => {
        const token = oAuthAssistant?.getToken()
        if (token) {
          dispatch(setAccessToken(token))
        }
      })
      .catch(() => {
        logAuthError(StepRefin.ERRO_AUTH_GET_TOKEN)
      })
  }, [dispatch, logAuthError])

  const authorize = useCallback(async () => {
    setIsLoading(true)

    try {
      await oAuthAssistant?.init()
      await oAuthAssistant?.authorize({
        scope: oAuthScopes,
        ui_locales: 'pt-BR',
      })
    } catch (error) {
      logAuthError(StepRefin.ERRO_AUTH_AUTHORIZE)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [logAuthError])

  const value = useMemo(
    () => ({
      accessToken,
      authorize,
      getToken,
      isLoading,
    }),
    [authorize, accessToken, getToken, isLoading],
  )

  return value
}

export default useAuth
